import { useState, useEffect } from "react";
import { type Category, type Subcategory } from "@/mocks/admin";
import AdminLayout from "@/components/feature/AdminLayout";
import { useCategoriesDB } from "@/hooks/useCategoriesDB";
import { useAdminI18n } from "@/hooks/useAdminI18n";

type ModalMode = "create" | "edit" | null;

const PRESET_COLORS = [
  "#8B0000", "#9f1239", "#be185d", "#059669", "#0d9488", "#0369a1",
  "#0891b2", "#7c3aed", "#d97706", "#ea580c", "#475569", "#0f766e",
];

const emptyCategory: Omit<Category, "id" | "articleCount"> = {
  name: "",
  slug: "",
  description: "",
  color: "#8B0000",
  imageUrl: "",
  isActive: true,
  order: 0,
  showInMenu: true,
  showOnHomepage: false,
  homepageOrder: 0,
  seoTitle: "",
  seoDescription: "",
  subcategories: [],
};

function generateSlug(name: string): string {
  return name.toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w-]/g, "")
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue");
}

export default function AdminCategories() {
  const { t } = useAdminI18n();
  const { categories: dbCategories, loading, error: dbError, refetch, createCategory, updateCategory, deleteCategory: dbDelete } = useCategoriesDB();
  const [categories, setCategories] = useState<Category[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setCategories(dbCategories);
  }, [dbCategories]);

  const [search, setSearch] = useState("");
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("general");
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);

  // Form state
  const [form, setForm] = useState<Omit<Category, "id" | "articleCount">>(emptyCategory);
  const [newSubName, setNewSubName] = useState("");
  const [newSubSlug, setNewSubSlug] = useState("");

  const filtered = categories.filter(
    (c) =>
      c.name.toLowerCase().includes(search.toLowerCase()) ||
      c.slug.toLowerCase().includes(search.toLowerCase())
  );

  const openCreate = () => {
    setForm({ ...emptyCategory, order: categories.length + 1, homepageOrder: categories.length + 1 });
    setEditingCategory(null);
    setModalMode("create");
    setActiveTab("general");
  };

  const openEdit = (cat: Category) => {
    setForm({
      name: cat.name,
      slug: cat.slug,
      description: cat.description,
      color: cat.color,
      imageUrl: cat.imageUrl || "",
      isActive: cat.isActive,
      order: cat.order,
      showInMenu: cat.showInMenu,
      showOnHomepage: cat.showOnHomepage,
      homepageOrder: cat.homepageOrder,
      seoTitle: cat.seoTitle || "",
      seoDescription: cat.seoDescription || "",
      subcategories: cat.subcategories || [],
    });
    setEditingCategory(cat);
    setModalMode("edit");
    setActiveTab("general");
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setSaveError(null);
    const slug = form.slug || generateSlug(form.name);
    if (modalMode === "create") {
      const result = await createCategory({ ...form, slug, articleCount: 0 });
      if (result.error) {
        setSaveError(result.error);
        setSaving(false);
        return;
      }
    } else if (modalMode === "edit" && editingCategory) {
      const result = await updateCategory(editingCategory.id, { ...form, slug });
      if (result.error) {
        setSaveError(result.error);
        setSaving(false);
        return;
      }
    }
    setSaving(false);
    setSaved(true);
    setTimeout(() => {
      setSaved(false);
      setModalMode(null);
    }, 800);
  };

  const handleDelete = (id: string) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (deleteId) {
      await dbDelete(deleteId);
    }
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const moveUp = (id: string) => {
    const idx = categories.findIndex((c) => c.id === id);
    if (idx <= 0) return;
    const newCats = [...categories];
    [newCats[idx - 1], newCats[idx]] = [newCats[idx], newCats[idx - 1]];
    setCategories(newCats.map((c, i) => ({ ...c, order: i + 1 })));
  };

  const moveDown = (id: string) => {
    const idx = categories.findIndex((c) => c.id === id);
    if (idx >= categories.length - 1) return;
    const newCats = [...categories];
    [newCats[idx], newCats[idx + 1]] = [newCats[idx + 1], newCats[idx]];
    setCategories(newCats.map((c, i) => ({ ...c, order: i + 1 })));
  };

  const addSubcategory = () => {
    if (!newSubName.trim()) return;
    const sub: Subcategory = {
      id: String(Date.now()),
      name: newSubName,
      slug: newSubSlug || newSubName.toLowerCase().replace(/\s+/g, "-").replace(/[^\w-]/g, ""),
      parentId: editingCategory?.id || "new",
    };
    setForm({ ...form, subcategories: [...(form.subcategories || []), sub] });
    setNewSubName("");
    setNewSubSlug("");
  };

  const removeSubcategory = (subId: string) => {
    setForm({ ...form, subcategories: (form.subcategories || []).filter((s) => s.id !== subId) });
  };

  const tabs = [
    { id: "general", label: t("categories.tab.general"), icon: "ri-settings-3-line" },
    { id: "seo", label: t("categories.tab.seo"), icon: "ri-search-line" },
    { id: "display", label: t("categories.tab.display"), icon: "ri-layout-line" },
    { id: "subcategories", label: t("categories.tab.subcategories"), icon: "ri-node-tree" },
  ];

  const yesLabel = t("common.yes");
  const noLabel = t("common.no");

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-hj-dark">{t("categories.title")}</h1>
            <div className="flex items-center gap-2 mt-1">
              <p className="text-sm text-hj-gray">{categories.length} {t("categories.total")}</p>
            </div>
          </div>
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            <i className="ri-add-line"></i>
            {t("categories.new")}
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: t("categories.stats.total"), value: categories.length, icon: "ri-folder-line", color: "text-hj-dark" },
            { label: t("categories.stats.active"), value: categories.filter((c) => c.isActive).length, icon: "ri-checkbox-circle-line", color: "text-emerald-600" },
            { label: t("categories.stats.inMenu"), value: categories.filter((c) => c.showInMenu).length, icon: "ri-menu-line", color: "text-sky-600" },
            { label: t("categories.stats.homepage"), value: categories.filter((c) => c.showOnHomepage).length, icon: "ri-home-line", color: "text-amber-600" },
          ].map((stat) => (
            <div key={stat.label} className="bg-white rounded-lg border border-hj-gray-border p-4">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg bg-hj-gray-light ${stat.color}`}>
                  <i className={`${stat.icon} text-lg`}></i>
                </div>
                <div>
                  <p className="text-xl font-bold text-hj-dark">{stat.value}</p>
                  <p className="text-xs text-hj-gray">{stat.label}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Supabase error banner */}
        {dbError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700 flex items-start gap-2">
            <i className="ri-error-warning-line shrink-0 mt-0.5"></i>
            <div>
              <strong>Supabase:</strong> {dbError}
            </div>
          </div>
        )}

        {/* Search */}
        <div className="bg-white rounded-lg border border-hj-gray-border p-4 mb-6">
          <div className="relative max-w-md">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-hj-gray"></i>
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t("categories.search")}
              className="w-full pl-9 pr-3 py-2 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
            />
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg border border-hj-gray-border p-12 text-center">
            <div className="w-8 h-8 border-2 border-hj-red border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-sm text-hj-gray">{t("common.loading")}</p>
          </div>
        )}

        {/* Table */}
        {!loading && (
          <div className="bg-white rounded-lg border border-hj-gray-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-hj-gray-light">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider w-16">{t("categories.col.order")}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider">{t("categories.col.category")}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider hidden sm:table-cell">{t("categories.col.slug")}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider hidden md:table-cell">{t("categories.col.subcategories")}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider">{t("categories.col.articles")}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider hidden lg:table-cell">{t("categories.col.menu")}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider hidden lg:table-cell">{t("categories.col.homepage")}</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-hj-gray uppercase tracking-wider">{t("categories.col.status")}</th>
                    <th className="px-4 py-3 text-right text-xs font-semibold text-hj-gray uppercase tracking-wider">{t("categories.col.actions")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-hj-gray-border">
                  {filtered.map((cat, idx) => (
                    <tr key={cat.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-3 py-3">
                        <div className="flex flex-col gap-0.5">
                          <button
                            onClick={() => moveUp(cat.id)}
                            disabled={idx === 0}
                            className="w-6 h-5 flex items-center justify-center rounded hover:bg-hj-gray-border transition-colors disabled:opacity-30"
                          >
                            <i className="ri-arrow-up-s-line text-xs text-hj-gray"></i>
                          </button>
                          <button
                            onClick={() => moveDown(cat.id)}
                            disabled={idx === filtered.length - 1}
                            className="w-6 h-5 flex items-center justify-center rounded hover:bg-hj-gray-border transition-colors disabled:opacity-30"
                          >
                            <i className="ri-arrow-down-s-line text-xs text-hj-gray"></i>
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          {cat.imageUrl ? (
                            <div className="w-10 h-10 rounded-md overflow-hidden shrink-0">
                              <img src={cat.imageUrl} alt={cat.name} className="w-full h-full object-cover" />
                            </div>
                          ) : (
                            <div className="w-10 h-10 rounded-md shrink-0 flex items-center justify-center" style={{ backgroundColor: cat.color + "20" }}>
                              <div className="w-4 h-4 rounded-full" style={{ backgroundColor: cat.color }}></div>
                            </div>
                          )}
                          <div>
                            <p className="text-sm font-semibold text-hj-dark">{cat.name}</p>
                            <p className="text-xs text-hj-gray line-clamp-1 max-w-[180px]">{cat.description}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-hj-gray hidden sm:table-cell font-mono">/{cat.slug}</td>
                      <td className="px-4 py-3 hidden md:table-cell">
                        <span className="text-sm text-hj-gray">{(cat.subcategories || []).length}</span>
                      </td>
                      <td className="px-4 py-3 text-sm text-hj-gray">{cat.articleCount}</td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium ${cat.showInMenu ? "text-emerald-600" : "text-gray-400"}`}>
                          <i className={`${cat.showInMenu ? "ri-check-line" : "ri-close-line"}`}></i>
                          {cat.showInMenu ? yesLabel : noLabel}
                        </span>
                      </td>
                      <td className="px-4 py-3 hidden lg:table-cell">
                        <span className={`inline-flex items-center gap-1 text-xs font-medium ${cat.showOnHomepage ? "text-emerald-600" : "text-gray-400"}`}>
                          <i className={`${cat.showOnHomepage ? "ri-check-line" : "ri-close-line"}`}></i>
                          {cat.showOnHomepage ? yesLabel : noLabel}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${cat.isActive ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                          {cat.isActive ? t("common.active") : t("common.inactive")}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <a
                            href={`/${cat.slug}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-hj-gray-light transition-colors"
                            title={t("common.preview")}
                          >
                            <i className="ri-eye-line text-hj-gray"></i>
                          </a>
                          <button
                            onClick={() => openEdit(cat)}
                            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-hj-gray-light transition-colors"
                            title={t("common.edit")}
                          >
                            <i className="ri-edit-line text-hj-gray"></i>
                          </button>
                          <button
                            onClick={() => handleDelete(cat.id)}
                            className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-red-50 transition-colors"
                            title={t("common.delete")}
                          >
                            <i className="ri-delete-bin-line text-red-500"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filtered.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-folder-line text-4xl text-hj-gray-border mb-3"></i>
                <p className="text-hj-gray">{t("categories.noFound")}</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {modalMode && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-white border-b border-hj-gray-border px-6 py-4 flex items-center justify-between rounded-t-xl">
              <h2 className="text-lg font-bold text-hj-dark">
                {modalMode === "create" ? t("categories.createTitle") : `"${editingCategory?.name}" ${t("categories.editTitle")}`}
              </h2>
              <button
                onClick={() => setModalMode(null)}
                className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-hj-gray-light transition-colors"
              >
                <i className="ri-close-line text-hj-dark"></i>
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 px-6 pt-4 pb-0">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 py-2 text-sm font-medium rounded-t-md transition-colors flex items-center gap-1.5 border-b-2 whitespace-nowrap ${
                    activeTab === tab.id
                      ? "text-hj-red border-hj-red"
                      : "text-hj-gray border-transparent hover:text-hj-dark"
                  }`}
                >
                  <i className={tab.icon}></i>
                  {tab.label}
                </button>
              ))}
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* General Tab */}
              {activeTab === "general" && (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-hj-dark mb-1.5">{t("categories.form.name")}</label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => {
                          setForm({
                            ...form,
                            name: e.target.value,
                            slug: form.slug || generateSlug(e.target.value),
                          });
                        }}
                        className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                        placeholder={t("categories.form.namePlaceholder")}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-hj-dark mb-1.5">{t("categories.form.slug")}</label>
                      <div className="flex items-center gap-1">
                        <span className="text-sm text-hj-gray shrink-0">/</span>
                        <input
                          type="text"
                          value={form.slug}
                          onChange={(e) => setForm({ ...form, slug: e.target.value })}
                          className="flex-1 px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-mono"
                          placeholder={t("categories.form.slugPlaceholder")}
                        />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-hj-dark mb-1.5">{t("categories.form.description")}</label>
                    <textarea
                      value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      rows={3}
                      maxLength={500}
                      className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red resize-none"
                      placeholder={t("categories.form.descriptionPlaceholder")}
                    />
                    <p className="text-xs text-hj-gray mt-1">{form.description.length}/500 {t("categories.form.descriptionCharCount")}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-hj-dark mb-1.5">{t("categories.form.imageUrl")}</label>
                    <input
                      type="url"
                      value={form.imageUrl || ""}
                      onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                      className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                      placeholder={t("categories.form.imageUrlPlaceholder")}
                    />
                    {form.imageUrl && (
                      <div className="mt-2 relative">
                        <img src={form.imageUrl} alt={t("categories.form.imagePreview")} className="w-full h-28 object-cover rounded-md" />
                        <button
                          onClick={() => setForm({ ...form, imageUrl: "" })}
                          className="absolute top-2 right-2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                        >
                          <i className="ri-close-line text-sm text-hj-dark"></i>
                        </button>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-hj-dark mb-2">{t("categories.form.color")}</label>
                    <div className="flex items-center gap-3 flex-wrap">
                      {PRESET_COLORS.map((color) => (
                        <button
                          key={color}
                          onClick={() => setForm({ ...form, color })}
                          className={`w-8 h-8 rounded-full transition-all ${form.color === color ? "ring-2 ring-offset-2 ring-hj-dark scale-110" : "hover:scale-105"}`}
                          style={{ backgroundColor: color }}
                        />
                      ))}
                      <div className="flex items-center gap-2 ml-2">
                        <input
                          type="color"
                          value={form.color}
                          onChange={(e) => setForm({ ...form, color: e.target.value })}
                          className="w-8 h-8 rounded cursor-pointer border border-hj-gray-border"
                        />
                        <input
                          type="text"
                          value={form.color}
                          onChange={(e) => setForm({ ...form, color: e.target.value })}
                          className="w-24 px-2 py-1.5 border border-hj-gray-border rounded-md text-xs font-mono focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-3 rounded-md bg-hj-gray-light">
                    <div>
                      <p className="text-sm font-medium text-hj-dark">{t("categories.form.activeToggle")}</p>
                      <p className="text-xs text-hj-gray">{t("categories.form.activeToggleDesc")}</p>
                    </div>
                    <button
                      onClick={() => setForm({ ...form, isActive: !form.isActive })}
                      className={`relative w-11 h-6 rounded-full transition-colors ${form.isActive ? "bg-hj-red" : "bg-gray-300"}`}
                    >
                      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.isActive ? "translate-x-5" : "translate-x-0.5"}`}></span>
                    </button>
                  </div>
                </>
              )}

              {/* SEO Tab */}
              {activeTab === "seo" && (
                <>
                  <div>
                    <label className="block text-sm font-semibold text-hj-dark mb-1.5">{t("categories.form.seoTitle")}</label>
                    <input
                      type="text"
                      value={form.seoTitle || ""}
                      onChange={(e) => setForm({ ...form, seoTitle: e.target.value })}
                      className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                      placeholder={t("categories.form.seoTitlePlaceholder")}
                    />
                    <p className="text-xs text-hj-gray mt-1">{(form.seoTitle || "").length}/60 {t("categories.form.seoTitleCharCount")}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-hj-dark mb-1.5">{t("categories.form.seoDescription")}</label>
                    <textarea
                      value={form.seoDescription || ""}
                      onChange={(e) => setForm({ ...form, seoDescription: e.target.value })}
                      rows={3}
                      maxLength={160}
                      className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red resize-none"
                      placeholder={t("categories.form.seoDescriptionPlaceholder")}
                    />
                    <p className="text-xs text-hj-gray mt-1">{(form.seoDescription || "").length}/160 {t("categories.form.seoDescriptionCharCount")}</p>
                  </div>

                  {/* Google Preview */}
                  <div>
                    <label className="block text-sm font-semibold text-hj-dark mb-2">{t("categories.form.googlePreview")}</label>
                    <div className="p-4 bg-white rounded-md border border-hj-gray-border">
                      <p className="text-sm text-hj-gray truncate">https://helvetik-journal.ch/{form.slug || "kategori"}</p>
                      <p className="text-base text-[#1a0dab] hover:underline cursor-pointer mt-0.5 line-clamp-1">
                        {form.seoTitle || form.name || t("categories.form.categoryNamePlaceholder")}
                      </p>
                      <p className="text-sm text-hj-gray mt-1 line-clamp-2">
                        {form.seoDescription || form.description || t("categories.form.descriptionShortPlaceholder")}
                      </p>
                    </div>
                  </div>
                </>
              )}

              {/* Display Tab */}
              {activeTab === "display" && (
                <>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-md bg-hj-gray-light">
                      <div>
                        <p className="text-sm font-medium text-hj-dark">{t("categories.form.showInMenu")}</p>
                        <p className="text-xs text-hj-gray">{t("categories.form.showInMenuDesc")}</p>
                      </div>
                      <button
                        onClick={() => setForm({ ...form, showInMenu: !form.showInMenu })}
                        className={`relative w-11 h-6 rounded-full transition-colors ${form.showInMenu ? "bg-hj-red" : "bg-gray-300"}`}
                      >
                        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.showInMenu ? "translate-x-5" : "translate-x-0.5"}`}></span>
                      </button>
                    </div>

                    <div className="flex items-center justify-between p-3 rounded-md bg-hj-gray-light">
                      <div>
                        <p className="text-sm font-medium text-hj-dark">{t("categories.form.showOnHomepage")}</p>
                        <p className="text-xs text-hj-gray">{t("categories.form.showOnHomepageDesc")}</p>
                      </div>
                      <button
                        onClick={() => setForm({ ...form, showOnHomepage: !form.showOnHomepage })}
                        className={`relative w-11 h-6 rounded-full transition-colors ${form.showOnHomepage ? "bg-hj-red" : "bg-gray-300"}`}
                      >
                        <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.showOnHomepage ? "translate-x-5" : "translate-x-0.5"}`}></span>
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-hj-dark mb-1.5">{t("categories.form.menuOrder")}</label>
                      <input
                        type="number"
                        value={form.order}
                        onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                        min={1}
                        className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-hj-dark mb-1.5">{t("categories.form.homepageOrder")}</label>
                      <input
                        type="number"
                        value={form.homepageOrder}
                        onChange={(e) => setForm({ ...form, homepageOrder: Number(e.target.value) })}
                        min={1}
                        className="w-full px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                      />
                    </div>
                  </div>

                  {/* Color Preview */}
                  <div>
                    <label className="block text-sm font-semibold text-hj-dark mb-2">{t("categories.form.headerPreview")}</label>
                    <div className="rounded-xl overflow-hidden">
                      {form.imageUrl ? (
                        <div className="relative h-24">
                          <img src={form.imageUrl} alt="" className="w-full h-full object-cover" />
                          <div className="absolute inset-0 flex items-center px-5" style={{ backgroundColor: form.color + "cc" }}>
                            <div>
                              <p className="text-white font-bold text-lg">{form.name || t("categories.form.categoryNamePlaceholder")}</p>
                              <p className="text-white/80 text-xs mt-0.5">{form.description || t("categories.form.descriptionShortPlaceholder")}</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="h-24 flex items-center px-5" style={{ backgroundColor: form.color }}>
                          <div>
                            <p className="text-white font-bold text-lg">{form.name || t("categories.form.categoryNamePlaceholder")}</p>
                            <p className="text-white/80 text-xs mt-0.5">{form.description || t("categories.form.descriptionShortPlaceholder")}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Subcategories Tab */}
              {activeTab === "subcategories" && (
                <>
                  <div className="space-y-2">
                    {(form.subcategories || []).map((sub) => (
                      <div key={sub.id} className="flex items-center gap-3 p-3 bg-hj-gray-light rounded-md">
                        <i className="ri-corner-down-right-line text-hj-gray text-sm"></i>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-hj-dark">{sub.name}</p>
                          <p className="text-xs text-hj-gray font-mono">/{sub.slug}</p>
                        </div>
                        <button
                          onClick={() => removeSubcategory(sub.id)}
                          className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-red-50 transition-colors shrink-0"
                        >
                          <i className="ri-delete-bin-line text-red-500 text-sm"></i>
                        </button>
                      </div>
                    ))}
                    {(form.subcategories || []).length === 0 && (
                      <p className="text-sm text-hj-gray text-center py-4">{t("categories.form.noSubcategories")}</p>
                    )}
                  </div>

                  <div className="border-t border-hj-gray-border pt-4">
                    <p className="text-sm font-semibold text-hj-dark mb-3">{t("categories.form.addSubcategory")}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <input
                        type="text"
                        value={newSubName}
                        onChange={(e) => {
                          setNewSubName(e.target.value);
                          if (!newSubSlug) setNewSubSlug(generateSlug(e.target.value));
                        }}
                        className="px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red"
                        placeholder={t("categories.form.subcategoryNamePlaceholder")}
                      />
                      <input
                        type="text"
                        value={newSubSlug}
                        onChange={(e) => setNewSubSlug(e.target.value)}
                        className="px-3 py-2.5 border border-hj-gray-border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-hj-red/20 focus:border-hj-red font-mono"
                        placeholder={t("categories.form.subcategorySlugPlaceholder")}
                      />
                    </div>
                    <button
                      onClick={addSubcategory}
                      disabled={!newSubName.trim()}
                      className="mt-3 px-4 py-2 text-sm font-medium text-hj-red border border-hj-red rounded-md hover:bg-hj-red/5 transition-colors disabled:opacity-40 flex items-center gap-2 whitespace-nowrap"
                    >
                      <i className="ri-add-line"></i>
                      {t("categories.form.addSubcategoryButton")}
                    </button>
                  </div>
                </>
              )}
            </div>

            {/* Save Error */}
            {saveError && (
              <div className="px-6 pb-2">
                <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  <strong>{t("common.error")}:</strong> {saveError}
                </div>
              </div>
            )}

            {/* Modal Footer */}
            <div className="sticky bottom-0 bg-white border-t border-hj-gray-border px-6 py-4 flex items-center justify-between rounded-b-xl">
              <button
                onClick={() => setModalMode(null)}
                className="px-4 py-2 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={handleSave}
                disabled={!form.name.trim() || saved || saving}
                className="px-5 py-2 bg-hj-red text-white text-sm font-medium rounded-md hover:bg-hj-red-light transition-colors disabled:opacity-50 flex items-center gap-2 whitespace-nowrap"
              >
                {saving ? (
                  <><span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>{t("common.loading")}</>
                ) : saved ? (
                  <><i className="ri-check-line"></i>{t("common.saved")}</>
                ) : (
                  <><i className="ri-save-line"></i>{modalMode === "create" ? t("common.create") : t("common.save")}</>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                <i className="ri-alert-line text-red-500 text-lg"></i>
              </div>
              <h3 className="text-lg font-bold text-hj-dark">{t("categories.deleteTitle")}</h3>
            </div>
            <p className="text-sm text-hj-gray mb-6">
              {t("categories.deleteConfirm")}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 text-sm font-medium text-hj-gray border border-hj-gray-border rounded-md hover:bg-gray-50 transition-colors whitespace-nowrap"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-500 rounded-md hover:bg-red-600 transition-colors whitespace-nowrap"
              >
                {t("common.delete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}